<template>
    <v-container class="pa-5" fluid>
        <AssignProductTemplateDialog
            :is-open="assignProductTemplateDialog"
            @on-save="onProductTemplateSelected"
            @on-close="closeAssignProductTemplateDialog"
        />

        <AssignComponentDialog
            :is-open="assignComponentDialog"
            :product-components="productComponentsList"
            @on-save="addProductComponent"
            @on-close="closeAssignComponentDialog"
        />

        <AssignProductionPropertiesDialog
            :is-open="assignProductionPropertiesDialog"
            :properties="productProductionPropertiesList"
            :component-properties="currentComponentProperties"
            @on-save="saveProductionProperties"
            @on-close="closeAssignProductionPropertiesDialog"
        />

        <AssignProductionProcessDialog
            :is-open="assignProductionProcessDialog"
            :processes="productionProcessesList"
            :component-processes="currentComponentProcesses"
            @on-save="saveProductionProcess"
            @on-close="closeAssignProductionProcessDialog"
        />

        <ConfirmDialog
            :is-open="confirmRemoveComponentDialog"
            @on-confirm="removeComponentConfirm"
            @on-abort="closeConfirmRemoveComponentDialog"
        >
            <template #title>
                {{ t('orders.internalOrders.product.dialogs.removeComponent.title') }}
            </template>
            <template #content>
                {{ t('orders.internalOrders.product.dialogs.removeComponent.content') }}
            </template>
        </ConfirmDialog>

        <ConfirmDialog :is-open="confirmLeaveDialog" @on-confirm="acceptLeave" @on-abort="closeConfirmLeaveDialog">
            <template #title>
                {{ t('orders.internalOrders.product.dialogs.leavePage.title') }}
            </template>
            <template #content>
                {{ t('orders.internalOrders.product.dialogs.leavePage.content') }}
            </template>
        </ConfirmDialog>

        <template v-if="isPageReady">
            <PageTitle>{{ pageTitle }}</PageTitle>

            <v-row>
                <v-col cols="12" xl="8" offset-xl="2">
                    <v-container>
                        <v-row align="center">
                            <v-col cols="auto">
                                <h2 class="text-h6">
                                    {{ t('orders.internalOrders.product.parametersTitle') }}
                                </h2>
                            </v-col>
                            <v-col v-if="isAddProduct" cols="auto">
                                <v-btn color="primary" variant="text" @click="openAssignProductTemplateDialog">
                                    {{ t('orders.internalOrders.product.selectFromTemplate') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-card>
                                    <v-container>
                                        <v-row>
                                            <v-col cols="12" md="4">
                                                <v-text-field
                                                    v-model="productTemplate.name"
                                                    :label="t('orders.internalOrders.product.name')"
                                                    variant="outlined"
                                                    density="compact"
                                                    :error-messages="nameErrors"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-autocomplete
                                                    v-model="productTemplate.jsonTemplate.productTypeId"
                                                    :items="productsList"
                                                    :label="t('orders.internalOrders.product.productType')"
                                                    item-title="name"
                                                    item-value="id"
                                                    variant="outlined"
                                                    density="compact"
                                                    :error-messages="productTypeErrors"
                                                    @update:model-value="handleProductTypeChange"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="2">
                                                <v-text-field
                                                    v-model="productTemplate.jsonTemplate.formatA"
                                                    :label="t('orders.internalOrders.product.width')"
                                                    suffix="mm"
                                                    active
                                                    variant="outlined"
                                                    density="compact"
                                                    type="number"
                                                    min="0"
                                                    :error-messages="formatAErrors"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="2">
                                                <v-text-field
                                                    v-model="productTemplate.jsonTemplate.formatB"
                                                    :label="t('orders.internalOrders.product.height')"
                                                    suffix="mm"
                                                    active
                                                    variant="outlined"
                                                    density="compact"
                                                    type="number"
                                                    min="0"
                                                    :error-messages="formatBErrors"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row align="center">
                                            <v-col cols="12" md="2">
                                                <v-text-field
                                                    v-model.number="product.amount"
                                                    :label="t('orders.internalOrders.product.amount')"
                                                    variant="outlined"
                                                    density="compact"
                                                    type="number"
                                                    min="1"
                                                    :error-messages="amountErrors"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="2">
                                                <v-text-field
                                                    v-model.number="product.numberOfVersions"
                                                    :label="t('orders.internalOrders.product.numberOfVersions')"
                                                    variant="outlined"
                                                    density="compact"
                                                    type="number"
                                                    min="1"
                                                    :disabled="!isAllowProofOrder"
                                                    :error-messages="numberOfVersionsErrors"
                                                />
                                            </v-col>
                                            <v-spacer />
                                            <v-col cols="12" md="auto" class="d-flex align-center">
                                                <v-checkbox
                                                    v-model="isSignal"
                                                    :label="t('orders.internalOrders.product.signalProduct')"
                                                    density="compact"
                                                    hide-details
                                                    class="mt-0"
                                                    @update:model-value="onSignalChange"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="2">
                                                <v-text-field
                                                    v-model.number="product.signalAmount"
                                                    :label="t('orders.internalOrders.product.signalAmount')"
                                                    suffix="szt."
                                                    active
                                                    variant="outlined"
                                                    density="compact"
                                                    type="number"
                                                    min="0"
                                                    :disabled="!isSignal"
                                                    :error-messages="signalAmountErrors"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row v-if="isProofOrPackagingPrototype">
                                            <v-col cols="12" md="4">
                                                <v-autocomplete
                                                    v-model="product.destinyPaperId"
                                                    :items="papersList"
                                                    :label="t('orders.internalOrders.product.destinyPaper')"
                                                    item-title="name"
                                                    item-value="id"
                                                    variant="outlined"
                                                    density="compact"
                                                    :error-messages="destinyPaperErrors"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12">
                                                <v-textarea
                                                    v-model="productTemplate.jsonTemplate.description"
                                                    :label="t('orders.internalOrders.product.notes')"
                                                    variant="outlined"
                                                    hide-details
                                                />
                                            </v-col>
                                            <v-col cols="12">
                                                <v-select
                                                    v-model="productTemplate.jsonTemplate.productionFileInPathId"
                                                    :items="productionFileInPathsList"
                                                    item-title="name"
                                                    item-value="id"
                                                    :label="t('orders.internalOrders.product.productionFilePath')"
                                                    variant="outlined"
                                                    density="compact"
                                                    clearable
                                                />
                                            </v-col>
                                            <v-col v-if="isAddProduct" cols="12" md="4">
                                                <v-checkbox
                                                    v-model="saveProductTemplate"
                                                    :label="t('orders.internalOrders.product.saveAsTemplate')"
                                                    hide-details
                                                    class="mt-0"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="auto">
                                <h2 class="text-h6">
                                    {{ t('orders.internalOrders.product.productionFiles') }}
                                </h2>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-card>
                                    <v-card-text>
                                        <v-radio-group v-model="selectedFileInput" class="mt-n4" hide-details>
                                            <v-row>
                                                <v-col cols="12" sm="8">
                                                    <v-radio
                                                        :label="t('orders.internalOrders.product.productionFileUploadLabel')"
                                                        value="upload"
                                                        class="font-weight-bold"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12" sm="8" md="6">
                                                    <v-file-input
                                                        v-model="currentProductionFile"
                                                        :label="t('orders.internalOrders.product.productionFileInputLabel')"
                                                        :disabled="selectedFileInput !== 'upload'"
                                                        show-size
                                                        :placeholder="
                                                            t('orders.internalOrders.product.productionFileInputPlaceholder')
                                                        "
                                                        :hint="t('orders.internalOrders.product.productionFileInputHint')"
                                                        persistent-hint
                                                        density="compact"
                                                        variant="outlined"
                                                        hide-details
                                                    />
                                                </v-col>
                                                <v-col cols="auto" class="d-flex align-center">
                                                    <v-btn
                                                        color="primary"
                                                        variant="outlined"
                                                        :disabled="!canUploadProductionFile() || productionFileUploading"
                                                        :loading="productionFileUploading"
                                                        @click="addProductionFileAttachment"
                                                    >
                                                        <v-icon start>mdi-file-upload</v-icon>
                                                        {{ t('orders.internalOrders.product.productionFileUploadSubmit') }}
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="selectedFileInput === 'upload' && productionFileSizeError()">
                                                <v-col cols="12">
                                                    <v-alert type="error" density="compact" variant="tonal">
                                                        {{ productionFileSizeError() }}
                                                    </v-alert>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12" sm="8">
                                                    <v-radio
                                                        :label="t('orders.internalOrders.product.productionFileLinkLabel')"
                                                        value="link"
                                                        class="font-weight-bold"
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12" sm="8" md="6">
                                                    <v-text-field
                                                        v-model="fileLink"
                                                        :label="t('orders.internalOrders.product.productionFileLinkFieldLabel')"
                                                        :disabled="selectedFileInput !== 'link'"
                                                        :placeholder="
                                                            t('orders.internalOrders.product.productionFileLinkPlaceholder')
                                                        "
                                                        density="compact"
                                                        variant="outlined"
                                                        hide-details
                                                    />
                                                </v-col>
                                                <v-col cols="auto" class="d-flex align-center">
                                                    <v-btn
                                                        color="primary"
                                                        variant="outlined"
                                                        :disabled="!fileLink?.trim() || productionFileUploading"
                                                        :loading="productionFileUploading"
                                                        @click="addProductionFileAttachmentLink"
                                                    >
                                                        <v-icon start>mdi-file-upload</v-icon>
                                                        {{ t('orders.internalOrders.product.productionFileUploadSubmit') }}
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </v-radio-group>
                                        <p class="text-caption text-medium-emphasis mt-2">
                                            {{
                                                t('orders.internalOrders.product.productionFileSizeLimit', {
                                                    max: productionFileSizeLimitMb
                                                })
                                            }}
                                        </p>
                                        <v-data-table
                                            v-if="(product.productionFiles?.length ?? 0) > 0"
                                            :items="product.productionFiles"
                                            :headers="productionFilesHeaders"
                                            hide-default-footer
                                            :mobile-breakpoint="0"
                                        >
                                            <template #item.fileName="{ item }">
                                                {{ productionFileName(item) }}
                                            </template>
                                            <template #item.actions="{ item }">
                                                <v-btn
                                                    icon
                                                    size="small"
                                                    variant="text"
                                                    :disabled="productionFileRemoving"
                                                    :aria-label="t('orders.internalOrders.product.removeProductionFile')"
                                                    @click="deleteProductionFileAttachment(item.id)"
                                                >
                                                    <v-icon color="error">mdi-delete</v-icon>
                                                </v-btn>
                                            </template>
                                        </v-data-table>
                                        <v-alert v-else type="info" variant="tonal" border="start">
                                            {{ t('orders.internalOrders.product.noProductionFiles') }}
                                        </v-alert>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12" class="d-flex align-center">
                                <v-btn color="error" class="me-2" :disabled="actionLoading" @click="goBack">
                                    {{ t('orders.internalOrders.product.back') }}
                                </v-btn>
                                <v-btn color="success" :disabled="actionLoading" @click="saveProduct">
                                    {{ t('orders.internalOrders.product.save') }}
                                </v-btn>
                            </v-col>
                        </v-row>

                        <v-row align="center">
                            <v-col cols="auto">
                                <h2 class="text-h6">
                                    {{ t('orders.internalOrders.product.componentsTitle') }}
                                </h2>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn color="secondary" icon size="small" @click="openAssignComponentDialog">
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col v-if="componentsLength === 0" cols="12">
                                <v-card>
                                    <v-card-text>
                                        <v-alert type="info" variant="tonal" border="start">
                                            {{ t('orders.internalOrders.product.noComponents') }}
                                        </v-alert>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <template v-else>
                                <v-col
                                    v-for="(component, componentIndex) in productTemplate.jsonTemplate.orderProductComponents"
                                    :key="componentIndex"
                                    cols="12"
                                >
                                    <v-card>
                                        <v-card-title>
                                            <v-row align="center">
                                                <v-col>
                                                    <span class="text-h6">
                                                        {{ component.component?.name }} ({{ component.component?.code }})
                                                    </span>
                                                </v-col>
                                                <v-col cols="auto">
                                                    <v-btn
                                                        color="primary"
                                                        variant="text"
                                                        size="small"
                                                        @click="openAssignProductionPropertiesDialog(componentIndex)"
                                                    >
                                                        {{ t('orders.internalOrders.product.addProductionProperties') }}
                                                    </v-btn>
                                                    <v-btn
                                                        color="primary"
                                                        variant="text"
                                                        size="small"
                                                        @click="openAssignProductionProcessDialog(componentIndex)"
                                                    >
                                                        {{ t('orders.internalOrders.product.addProductionProcesses') }}
                                                    </v-btn>
                                                    <v-btn
                                                        icon
                                                        size="small"
                                                        variant="text"
                                                        :title="t('orders.internalOrders.product.copyComponent')"
                                                        @click="copyComponent(component)"
                                                    >
                                                        <v-icon>mdi-content-copy</v-icon>
                                                    </v-btn>
                                                    <v-btn
                                                        icon
                                                        size="small"
                                                        variant="text"
                                                        @click="showConfirmRemoveComponent(componentIndex)"
                                                    >
                                                        <v-icon color="error">mdi-delete</v-icon>
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </v-card-title>
                                        <v-card-text>
                                            <v-row v-if="component.component?.code === 'skladka'">
                                                <v-col cols="12" sm="8" lg="8">
                                                    <div class="d-flex align-center flex-wrap gap-2">
                                                        <v-text-field
                                                            v-model="component.numberOfCopies"
                                                            type="number"
                                                            min="0"
                                                            :label="t('orders.internalOrders.product.numberOfSignatures')"
                                                            :placeholder="
                                                                t('orders.internalOrders.product.numberOfSignaturesPlaceholder')
                                                            "
                                                            variant="outlined"
                                                            density="compact"
                                                            hide-details
                                                            class="flex-grow-0 me-2"
                                                            style="max-width: 12rem"
                                                            @change="handleNumberOfCopiesInput"
                                                            @focus="onNumberOfCopiesFocus(component)"
                                                        />
                                                        <span class="font-weight-bold text-caption text-no-wrap">
                                                            {{ calculatePageRange(component) }}
                                                        </span>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-alert
                                                v-if="!component.orderComponentProperties?.length"
                                                type="info"
                                                variant="tonal"
                                                border="start"
                                                density="compact"
                                            >
                                                {{ t('orders.internalOrders.product.noProductionProperties') }}
                                            </v-alert>
                                            <v-row v-else>
                                                <v-col cols="12">
                                                    <strong>{{
                                                        t('orders.internalOrders.product.productionPropertiesLabel')
                                                    }}</strong>
                                                </v-col>
                                                <v-col
                                                    v-for="(
                                                        productionProperty, propertyIndex
                                                    ) in component.orderComponentProperties"
                                                    :key="productionProperty.productProductionProperty?.id ?? propertyIndex"
                                                    cols="12"
                                                    sm="6"
                                                    lg="4"
                                                >
                                                    <div class="d-flex align-center gap-1">
                                                        <v-autocomplete
                                                            v-if="
                                                                productionProperty.productProductionProperty?.name ===
                                                                PRODUCTION_PROPERTY_PAPER_TYPE_NAME
                                                            "
                                                            v-model="productionProperty.value"
                                                            :items="papersList"
                                                            :label="
                                                                (productionProperty.productProductionProperty?.verboseName ??
                                                                    '') + ':'
                                                            "
                                                            :placeholder="t('orders.internalOrders.product.valuePlaceholder')"
                                                            item-title="name"
                                                            item-value="name"
                                                            variant="outlined"
                                                            density="compact"
                                                            class="flex-grow-1"
                                                            hide-details="auto"
                                                            :error-messages="
                                                                productionPropertyValueErrors(componentIndex, propertyIndex)
                                                            "
                                                        />
                                                        <v-autocomplete
                                                            v-else-if="
                                                                productionProperty.productProductionProperty?.name ===
                                                                PRODUCTION_PROPERTY_FOLDING_TYPE_NAME
                                                            "
                                                            v-model="productionProperty.value"
                                                            :items="foldingTypesList"
                                                            :label="
                                                                (productionProperty.productProductionProperty?.verboseName ??
                                                                    '') + ':'
                                                            "
                                                            :placeholder="t('orders.internalOrders.product.valuePlaceholder')"
                                                            item-title="name"
                                                            item-value="name"
                                                            variant="outlined"
                                                            density="compact"
                                                            class="flex-grow-1"
                                                            hide-details="auto"
                                                            :error-messages="
                                                                productionPropertyValueErrors(componentIndex, propertyIndex)
                                                            "
                                                            @update:model-value="handleFoldingTypeChange(productionProperty)"
                                                        />
                                                        <v-text-field
                                                            v-else-if="
                                                                productionProperty.productProductionProperty?.name ===
                                                                PRODUCTION_PROPERTY_FOLDING_COUNT_NAME
                                                            "
                                                            v-model="productionProperty.value"
                                                            type="number"
                                                            min="1"
                                                            :label="
                                                                (productionProperty.productProductionProperty?.verboseName ??
                                                                    '') + ':'
                                                            "
                                                            :placeholder="t('orders.internalOrders.product.valuePlaceholder')"
                                                            variant="outlined"
                                                            density="compact"
                                                            class="flex-grow-1"
                                                            hide-details="auto"
                                                            :error-messages="
                                                                productionPropertyValueErrors(componentIndex, propertyIndex)
                                                            "
                                                            @update:model-value="
                                                                enforceMinValue(componentIndex, propertyIndex, 1)
                                                            "
                                                        />
                                                        <v-text-field
                                                            v-else
                                                            v-model="productionProperty.value"
                                                            :label="
                                                                (productionProperty.productProductionProperty?.verboseName ??
                                                                    '') + ':'
                                                            "
                                                            :placeholder="t('orders.internalOrders.product.valuePlaceholder')"
                                                            variant="outlined"
                                                            density="compact"
                                                            class="flex-grow-1"
                                                            hide-details="auto"
                                                            :error-messages="
                                                                productionPropertyValueErrors(componentIndex, propertyIndex)
                                                            "
                                                        />
                                                        <v-btn
                                                            icon
                                                            size="small"
                                                            variant="text"
                                                            :aria-label="t('orders.internalOrders.product.removeProperty')"
                                                            @click="
                                                                removeProductProductionProperty(
                                                                    componentIndex,
                                                                    productionProperty
                                                                )
                                                            "
                                                        >
                                                            <v-icon>mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>

                                        <v-divider />

                                        <v-card-text>
                                            <v-alert
                                                v-if="!component.orderComponentProcesses?.length"
                                                type="info"
                                                variant="tonal"
                                                border="start"
                                                density="compact"
                                            >
                                                {{ t('orders.internalOrders.product.noProductionProcesses') }}
                                            </v-alert>
                                            <v-row v-else>
                                                <v-col cols="12">
                                                    <strong>{{
                                                        t('orders.internalOrders.product.productionProcessesLabel')
                                                    }}</strong>
                                                </v-col>
                                                <v-col cols="12">
                                                    <v-chip
                                                        v-for="productionProcess in component.orderComponentProcesses"
                                                        :key="
                                                            productionProcess.productionProcess?.id ??
                                                            productionProcess.productionProcessId
                                                        "
                                                        class="ma-1"
                                                        closable
                                                        @click:close="removeProductionProcess(componentIndex, productionProcess)"
                                                    >
                                                        {{ productionProcess.productionProcess?.name ?? '' }}
                                                    </v-chip>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </template>
                        </v-row>
                    </v-container>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup lang="ts">
// @ts-nocheck — głębokie struktury szablonu/komponentów (legacy); pełna inferencja w kolejnym refaktorze
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { ref, reactive, computed, onBeforeMount } from 'vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { integer, minValue, required, requiredIf } from '@vuelidate/validators'
import { storeToRefs } from 'pinia'
import { useInternalOrdersStore } from '@/stores/orders/internalOrders'
import { useProductsStore } from '@/stores/configurations/products'
import { useMaterialsStore } from '@/stores/configurations/materials'
import { useProductTemplatesStore } from '@/stores/orders/productTemplates'
import { useProductsService } from '@/views/Configurations/Products/services/productsService'
import { useProductTemplatesService } from '@/views/Orders/ProductTemplates/services/productTemplatesService'
import { usePapersService } from '@/views/Configurations/Materials/services/papersService'
import {
    ORDERS,
    PRODUCTION_PROPERTY_FOLDING_COUNT_NAME,
    PRODUCTION_PROPERTY_FOLDING_TYPE_NAME,
    PRODUCTION_PROPERTY_PAPER_TYPE_NAME
} from '@/constants'
import internalOrdersQueries from '@/api/modules/internalOrdersQueries'

import type { InternalOrderProductionFileAttachment } from '@/types/domain/orders/internal'
import { errorHandler } from '@/composables/errorHandler'
import { isValidProductsInSetCombination } from '@/helpers/productTypeHelpers'
import { notificationService } from '@/services/notificationService'
import PageTitle from '@/components/common/PageTitle.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import AssignProductTemplateDialog from '@/views/Orders/InternalOrders/AssignProductTemplateDialog.vue'
import AssignComponentDialog from '@/views/Configurations/Products/Product/ProductComponentsConfigurator/AssignComponentDialog.vue'
import AssignProductionPropertiesDialog from '@/views/Configurations/Products/Product/ProductComponentsConfigurator/AssignProductionPropertiesDialog.vue'
import AssignProductionProcessDialog from '@/views/Configurations/Products/Product/ProductComponentsConfigurator/AssignProductionProcessDialog.vue'
import {
    prepareOrderProductComponentsForCreate,
    normalizeProductDimension
} from '@/views/Orders/InternalOrders/helpers/orderCreateRequestHelpers'
import { prefetchOrderProductPageData } from '@/views/Orders/InternalOrders/helpers/prefetchOrderProductPageData'

// 2. Props / Emits
// (none)

// 3. Constants
type ProductForm = {
    amount: number | null
    numberOfVersions: number
    weight: number
    productionFiles: InternalOrderProductionFileAttachment[]
    destinyPaperId: number | null
    signalAmount: number
}

/** Zagnieżdżony szablon produktu — pozostawiamy elastyczność jak w legacy */
type ProductTemplateForm = Record<string, any>

const EMPTY_PRODUCT = (): ProductForm => ({
    amount: null,
    numberOfVersions: 1,
    weight: 0,
    productionFiles: [],
    destinyPaperId: null,
    signalAmount: 0
})

const EMPTY_PRODUCT_TEMPLATE = (): ProductTemplateForm => ({
    name: null,
    tag: null,
    jsonTemplate: {
        productionFileInPathId: null,
        productTypeId: null,
        formatA: null,
        formatB: null,
        orderProductComponents: [],
        description: null
    }
})

// 4. Stores & top-level services
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const internalOrdersStore = useInternalOrdersStore()
const productsStore = useProductsStore()
const materialsStore = useMaterialsStore()
const productTemplatesStore = useProductTemplatesStore()

const productsService = useProductsService({ store: productsStore, translate: t })
const productTemplatesService = useProductTemplatesService({ store: productTemplatesStore })
const papersService = usePapersService({ store: materialsStore, translate: t })

const { orderCreate } = storeToRefs(internalOrdersStore)
const { productComponents, productProductionProperties, productionProcesses, products } = storeToRefs(productsStore)
const { papers } = storeToRefs(materialsStore)
const { productionFileInPaths: productionFileInPathsList, foldingTypes: foldingTypesList } = storeToRefs(productTemplatesStore)

// 5. Reactive state
const editIndex = computed((): number | undefined => {
    const paramIndex = route.params.index
    return typeof paramIndex !== 'undefined' && paramIndex !== '' ? Number(paramIndex) : undefined
})

const isAddProduct = computed((): boolean => editIndex.value === undefined)

const product = reactive<ProductForm>({ ...EMPTY_PRODUCT() })
const productTemplate = reactive<ProductTemplateForm>(JSON.parse(JSON.stringify(EMPTY_PRODUCT_TEMPLATE())))

const isSignal = ref(false)
const saveProductTemplate = ref(false)
const assignProductTemplateDialog = ref(false)
const assignComponentDialog = ref(false)
const assignProductionPropertiesDialog = ref(false)
const assignProductionProcessDialog = ref(false)
const confirmRemoveComponentDialog = ref(false)
const confirmLeaveDialog = ref(false)
const confirmLeaveNext = ref<RouteLocationNormalized | null>(null)
const currentProductionComponentIndex = ref<number | null>(null)
const actionLoading = ref(false)
const hasProductChanges = ref(false)
const pageTitle = ref('')
const isPageReady = ref(false)
const submitted = ref(false)
const userEditedComponents = ref(false)
const selectedFileInput = ref('upload')
const currentProductionFile = ref<File | null>(null)
const fileLink = ref('')
const productionFileUploading = ref(false)
const productionFileRemoving = ref(false)

const productionFilesHeaders = computed(() => [
    { title: t('orders.internalOrders.product.productionFileName'), key: 'fileName', sortable: false },
    { title: '', key: 'actions', sortable: false, width: '80px' }
])

// 6. Validation rules & setup
type ProductTypeListItem = {
    id: number
    allowProofOrder?: boolean
    allowPackagePrototypeOrder?: boolean
    productTypeComponent?: unknown[]
}

const productsList = computed((): ProductTypeListItem[] => (products.value ?? []) as ProductTypeListItem[])
const selectedProductType = computed((): ProductTypeListItem | undefined => {
    const id = productTemplate.jsonTemplate.productTypeId
    if (id == null) return undefined
    return productsList.value.find((p) => p.id === id)
})
const isAllowProofOrder = computed((): boolean => !!selectedProductType.value?.allowProofOrder)
const isAllowPackagePrototypeOrder = computed((): boolean => !!selectedProductType.value?.allowPackagePrototypeOrder)
const isProofOrPackagingPrototype = computed((): boolean => isAllowProofOrder.value || isAllowPackagePrototypeOrder.value)

const templateRules = computed(() => ({
    name: { required },
    jsonTemplate: {
        productTypeId: { required },
        formatA: { required, integer },
        formatB: { required, integer }
    }
}))

// Legacy: signalAmount walidowane tylko gdy isSignal (checkbox włączony)
const productRules = computed(() => {
    const rules: Record<string, unknown> = {
        amount: { required, minValue: minValue(1) },
        numberOfVersions: { required, minValue: minValue(1) },
        destinyPaperId: { requiredIf: requiredIf(() => isProofOrPackagingPrototype.value) }
    }
    if (isSignal.value) {
        rules.signalAmount = { required, minValue: minValue(1) }
    }
    return rules
})

const v$Template = useVuelidate(templateRules, productTemplate as never)
const v$Product = useVuelidate(productRules, product as never)

const requiredMessage = (): string => t('orders.internalOrders.product.validations.requiredField')
const shouldShowFieldErrors = (fieldValidation?: { $dirty?: boolean } | null): boolean =>
    submitted.value || !!fieldValidation?.$dirty

const nameErrors = computed(() => {
    const fieldValidation = v$Template.value?.name
    if (!shouldShowFieldErrors(fieldValidation)) return []
    return fieldValidation?.required?.$invalid ? [requiredMessage()] : []
})

const productTypeErrors = computed(() => {
    const fieldValidation = v$Template.value?.jsonTemplate?.productTypeId
    if (!shouldShowFieldErrors(fieldValidation)) return []
    if (fieldValidation?.required?.$invalid) return [requiredMessage()]
    if (!isProductTypeCombinationValid.value) {
        return [t('orders.internalOrders.product.validations.productsInSetCombination')]
    }
    return []
})

const amountErrors = computed(() => {
    const fieldValidation = v$Product.value?.amount
    if (!shouldShowFieldErrors(fieldValidation)) return []
    const errors = []
    if (fieldValidation?.required?.$invalid) errors.push(requiredMessage())
    if (fieldValidation?.minValue?.$invalid) errors.push(t('orders.internalOrders.product.validations.amountMin'))
    return errors
})

const formatAErrors = computed(() => {
    const fieldValidation = v$Template.value?.jsonTemplate?.formatA
    if (!shouldShowFieldErrors(fieldValidation)) return []
    const errors = []
    if (fieldValidation?.required?.$invalid) errors.push(requiredMessage())
    if (fieldValidation?.integer?.$invalid) errors.push(t('orders.internalOrders.product.validations.invalidValue'))
    return errors
})

const formatBErrors = computed(() => {
    const fieldValidation = v$Template.value?.jsonTemplate?.formatB
    if (!shouldShowFieldErrors(fieldValidation)) return []
    const errors = []
    if (fieldValidation?.required?.$invalid) errors.push(requiredMessage())
    if (fieldValidation?.integer?.$invalid) errors.push(t('orders.internalOrders.product.validations.invalidValue'))
    return errors
})

const numberOfVersionsErrors = computed(() => {
    const fieldValidation = v$Product.value?.numberOfVersions
    if (!shouldShowFieldErrors(fieldValidation)) return []
    const errors = []
    if (fieldValidation?.required?.$invalid) errors.push(requiredMessage())
    if (fieldValidation?.minValue?.$invalid) errors.push(t('orders.internalOrders.product.validations.numberOfVersionsMin'))
    return errors
})

const destinyPaperErrors = computed(() => {
    const fieldValidation = v$Product.value?.destinyPaperId
    if (!shouldShowFieldErrors(fieldValidation)) return []
    return fieldValidation?.requiredIf?.$invalid ? [requiredMessage()] : []
})

const signalAmountErrors = computed(() => {
    if (!isSignal.value) return []
    const fieldValidation = v$Product.value?.signalAmount
    if (!shouldShowFieldErrors(fieldValidation)) return []
    const errors = []
    if (fieldValidation?.required?.$invalid) errors.push(requiredMessage())
    if (fieldValidation?.minValue?.$invalid) errors.push(t('orders.internalOrders.product.validations.signalAmountMin'))
    return errors
})

// 7. Computed
const papersList = computed(() => papers.value ?? [])
const productComponentsList = computed(() => productComponents.value ?? [])
const productProductionPropertiesList = computed(() => productProductionProperties.value ?? [])
const productionProcessesList = computed(() => productionProcesses.value ?? [])
const isProductTypeCombinationValid = computed((): boolean => {
    const existingProductTypeIds = (orderCreate.value?.order?.orderProducts ?? [])
        .filter((_, index) => index !== editIndex.value)
        .map((item) => item.productTypeId)

    return isValidProductsInSetCombination([...existingProductTypeIds, productTemplate.jsonTemplate.productTypeId])
})

const componentsLength = computed(() => productTemplate.jsonTemplate.orderProductComponents?.length ?? 0)

const productionFileSizeLimitMb = computed(() =>
    isProofOrPackagingPrototype.value
        ? ORDERS.PRODUCTION_FILE_ATTACHMENT_SIZE_PROOF_MB
        : ORDERS.PRODUCTION_FILE_ATTACHMENT_SIZE_MB
)

const maxProductionFileSizeBytes = computed(() => productionFileSizeLimitMb.value * 1024 * 1024)

function canUploadProductionFile(): boolean {
    if (isAllowProofOrder.value && (product.productionFiles?.length ?? 0) > 0) return false
    const file = currentProductionFile.value
    if (!file) return false
    return file.size <= maxProductionFileSizeBytes.value
}

function productionFileSizeError(): string | null {
    const file = currentProductionFile.value
    if (!file || file.size <= maxProductionFileSizeBytes.value) return null
    return t('orders.internalOrders.product.validations.productionFileSize', {
        max: productionFileSizeLimitMb.value
    })
}

const currentProductionComponent = computed(() => {
    const componentIndex = currentProductionComponentIndex.value
    if (componentIndex == null) return null
    return productTemplate.jsonTemplate.orderProductComponents?.[componentIndex] ?? null
})

const currentComponentProperties = computed(() => currentProductionComponent.value?.orderComponentProperties ?? [])

const currentComponentProcesses = computed(() => currentProductionComponent.value?.orderComponentProcesses ?? [])

// 8. Handlers / methods
async function openAssignProductTemplateDialog(): Promise<void> {
    try {
        await productTemplatesService.fetchProductTemplatesByQueryString('')
    } catch {
        // ignore – dialog can still open with empty list
    }
    assignProductTemplateDialog.value = true
}

function closeAssignProductTemplateDialog(): void {
    assignProductTemplateDialog.value = false
}

function assignSequentialIds(): void {
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    const signatureComponents = components.filter((c) => c.component?.code === 'skladka')
    signatureComponents.forEach((component, index) => {
        component.sequentialId = index + 1
    })
}

function calculatePageRange(component: unknown): string {
    if (!component?.sequentialId) assignSequentialIds()
    if (!component?.numberOfCopies || parseInt(component.numberOfCopies, 10) === 0) {
        return t('orders.internalOrders.product.signatureRangeEmpty')
    }
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    const signatureComponents = components.filter((c) => c.component?.code === 'skladka')
    const sorted = [...signatureComponents].sort((a, b) => (a.sequentialId ?? 0) - (b.sequentialId ?? 0))
    let startPage = 1
    const currentComponentIndex = sorted.findIndex((c) => c === component)
    for (let i = 0; i < currentComponentIndex; i++) {
        const previousComponent = sorted[i]
        const copiesCount = parseInt(previousComponent?.numberOfCopies, 10)
        if (copiesCount > 0) startPage += copiesCount
    }
    const currentQuantity = parseInt(component.numberOfCopies, 10) || 1
    const endPage = startPage + currentQuantity - 1
    return t('orders.internalOrders.product.signatureRange', { start: startPage, end: endPage })
}

function handleNumberOfCopiesInput(): void {
    hasProductChanges.value = true
    assignSequentialIds()
}

function onNumberOfCopiesFocus(component: { numberOfCopies?: string | number }): void {
    if (!Number(component?.numberOfCopies)) {
        component.numberOfCopies = ''
    }
}

function enforceMinValue(componentIndex: number, propertyIndex: number, minValue: number): void {
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    const component = components[componentIndex]
    const properties = component?.orderComponentProperties ?? []
    const property = properties[propertyIndex]
    if (!property) return
    const value = parseInt(property.value, 10)
    if (!Number.isNaN(value) && value < minValue) {
        property.value = String(minValue)
    }
}

function handleFoldingTypeChange(productionProperty: { value?: string }): void {
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    const componentIndex = components.findIndex((c) => (c.orderComponentProperties ?? []).includes(productionProperty))
    if (componentIndex === -1) return
    const component = components[componentIndex]
    const foldingType = foldingTypesList.value.find((type) => type.name === productionProperty.value)
    const foldingCountProp = (component.orderComponentProperties ?? []).find(
        (p) => p.productProductionProperty?.name === PRODUCTION_PROPERTY_FOLDING_COUNT_NAME
    )
    const foldingCountDef = productProductionPropertiesList.value.find((p) => p.name === PRODUCTION_PROPERTY_FOLDING_COUNT_NAME)
    if (foldingType?.numberOfFoldingsRequired) {
        if (!foldingCountProp && foldingCountDef) {
            if (!component.orderComponentProperties) component.orderComponentProperties = []
            component.orderComponentProperties.push({
                productProductionPropertyId: foldingCountDef.id,
                productProductionProperty: foldingCountDef,
                value: ''
            })
        }
    } else {
        const foldingCountIndex = (component.orderComponentProperties ?? []).findIndex(
            (p) => p.productProductionProperty?.name === PRODUCTION_PROPERTY_FOLDING_COUNT_NAME
        )
        if (foldingCountIndex !== -1) component.orderComponentProperties.splice(foldingCountIndex, 1)
    }
    hasProductChanges.value = true
}

function removeProductProductionProperty(componentIndex: number, productionProperty: unknown): void {
    const component = productTemplate.jsonTemplate.orderProductComponents?.[componentIndex]
    if (!component?.orderComponentProperties) return
    const propertyIndex = component.orderComponentProperties.indexOf(productionProperty)
    if (propertyIndex !== -1) component.orderComponentProperties.splice(propertyIndex, 1)
    hasProductChanges.value = true
}

function removeProductionProcess(componentIndex: number, productionProcess: unknown): void {
    const component = productTemplate.jsonTemplate.orderProductComponents?.[componentIndex]
    if (!component?.orderComponentProcesses) return
    const processIndex = component.orderComponentProcesses.indexOf(productionProcess)
    if (processIndex !== -1) component.orderComponentProcesses.splice(processIndex, 1)
    hasProductChanges.value = true
}

function productionPropertyValueErrors(componentIndex: number, propertyIndex: number): string[] {
    if (!submitted.value) return []
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    const component = components[componentIndex]
    const properties = component?.orderComponentProperties ?? []
    const prop = properties[propertyIndex]
    if (!prop) return []
    const propertyValue = prop.value
    if (propertyValue == null || String(propertyValue ?? '').trim() === '') {
        return [requiredMessage()]
    }
    return []
}

function copyComponent(component: unknown): void {
    userEditedComponents.value = true
    hasProductChanges.value = true
    const componentCopy = JSON.parse(JSON.stringify(component))
    if (componentCopy.component?.code === 'skladka') {
        componentCopy.numberOfCopies = 0
    }
    productTemplate.jsonTemplate.orderProductComponents.push(componentCopy)
    assignSequentialIds()
}

function hydrateTemplateComponents(): void {
    const propsList = productProductionPropertiesList.value
    const procsList = productionProcessesList.value
    const componentsCatalog = productComponentsList.value
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    components.forEach((comp) => {
        if (!comp.component && comp.componentId != null) {
            const found = componentsCatalog.find((catalogComponent) => catalogComponent.id === comp.componentId)
            if (found) comp.component = found
        }
        ;(comp.orderComponentProperties ?? []).forEach((item) => {
            if (!item.productProductionProperty && item.productProductionPropertyId != null) {
                const found = propsList.find((p) => p.id === item.productProductionPropertyId)
                if (found) item.productProductionProperty = found
            }
        })
        ;(comp.orderComponentProcesses ?? []).forEach((item) => {
            if (!item.productionProcess && item.productionProcessId != null) {
                const found = procsList.find((p) => p.id === item.productionProcessId)
                if (found) item.productionProcess = found
            }
        })
    })
}

async function onProductTemplateSelected(payload: { productTemplateId?: number | null }): Promise<void> {
    if (!payload?.productTemplateId) {
        closeAssignProductTemplateDialog()
        return
    }
    try {
        const response = await productTemplatesService.fetchProductTemplate(payload.productTemplateId)
        const data = response.data
        productTemplate.name = data.name ?? null
        productTemplate.tag = data.tag ?? null
        let json = data.jsonTemplate
        if (typeof json === 'string') {
            try {
                json = JSON.parse(json)
            } catch {
                json = {}
            }
        }
        if (json && typeof json === 'object') {
            productTemplate.jsonTemplate.productionFileInPathId = json.productionFileInPathId ?? null
            productTemplate.jsonTemplate.productTypeId = json.productTypeId ?? null
            productTemplate.jsonTemplate.formatA = normalizeProductDimension(json.formatA)
            productTemplate.jsonTemplate.formatB = normalizeProductDimension(json.formatB)
            productTemplate.jsonTemplate.description = json.description ?? null
            productTemplate.jsonTemplate.orderProductComponents = Array.isArray(json.orderProductComponents)
                ? JSON.parse(JSON.stringify(json.orderProductComponents))
                : []
        }
        hydrateTemplateComponents()
        assignSequentialIds()
        clearDestinyPaper()
        hasProductChanges.value = true
    } catch (err) {
        errorHandler(err)
    }
    closeAssignProductTemplateDialog()
}

function openAssignComponentDialog(): void {
    assignComponentDialog.value = true
}

function closeAssignComponentDialog(): void {
    assignComponentDialog.value = false
}

function addProductComponent(component: { id: number; code?: string }): void {
    if (!component) return
    const newComponent = {
        component,
        componentId: component.id,
        orderComponentProcesses: [],
        orderComponentProperties: []
    }
    if (component.code === 'skladka') {
        newComponent.numberOfCopies = 0
    }
    productTemplate.jsonTemplate.orderProductComponents.push(newComponent)
    userEditedComponents.value = true
    assignSequentialIds()
    hasProductChanges.value = true
    closeAssignComponentDialog()
}

function openAssignProductionPropertiesDialog(componentIndex: number): void {
    currentProductionComponentIndex.value = componentIndex
    assignProductionPropertiesDialog.value = true
}

function closeAssignProductionPropertiesDialog(): void {
    assignProductionPropertiesDialog.value = false
    currentProductionComponentIndex.value = null
}

function saveProductionProperties(properties: Array<{ id: number; defaultValue?: string }>): void {
    const componentIndex = currentProductionComponentIndex.value
    if (componentIndex == null || !productTemplate.jsonTemplate.orderProductComponents[componentIndex]) return
    const component = productTemplate.jsonTemplate.orderProductComponents[componentIndex]
    if (!component.orderComponentProperties) component.orderComponentProperties = []
    properties.forEach((prop) => {
        component.orderComponentProperties.push({
            productProductionPropertyId: prop.id,
            productProductionProperty: prop,
            value: ''
        })
    })
    hasProductChanges.value = true
    closeAssignProductionPropertiesDialog()
}

function openAssignProductionProcessDialog(componentIndex: number): void {
    currentProductionComponentIndex.value = componentIndex
    assignProductionProcessDialog.value = true
}

function closeAssignProductionProcessDialog(): void {
    assignProductionProcessDialog.value = false
    currentProductionComponentIndex.value = null
}

function saveProductionProcess(processes: Array<{ id: number }>): void {
    const componentIndex = currentProductionComponentIndex.value
    if (componentIndex == null || !productTemplate.jsonTemplate.orderProductComponents[componentIndex]) return
    const component = productTemplate.jsonTemplate.orderProductComponents[componentIndex]
    if (!component.orderComponentProcesses) component.orderComponentProcesses = []
    processes.forEach((process, index) => {
        component.orderComponentProcesses.push({
            order: index + 1,
            productionProcessId: process.id,
            productionProcess: process
        })
    })
    hasProductChanges.value = true
    closeAssignProductionProcessDialog()
}

function showConfirmRemoveComponent(componentIndex: number): void {
    currentProductionComponentIndex.value = componentIndex
    confirmRemoveComponentDialog.value = true
}

function closeConfirmRemoveComponentDialog(): void {
    confirmRemoveComponentDialog.value = false
    currentProductionComponentIndex.value = null
}

function removeComponentConfirm(): void {
    const componentIndex = currentProductionComponentIndex.value
    if (componentIndex != null && productTemplate.jsonTemplate.orderProductComponents?.length) {
        productTemplate.jsonTemplate.orderProductComponents.splice(componentIndex, 1)
        userEditedComponents.value = true
        assignSequentialIds()
        hasProductChanges.value = true
    }
    closeConfirmRemoveComponentDialog()
}

function closeConfirmLeaveDialog(): void {
    confirmLeaveDialog.value = false
    confirmLeaveNext.value = null
}

function acceptLeave(): void {
    const next = confirmLeaveNext.value
    closeConfirmLeaveDialog()
    hasProductChanges.value = false
    if (next) {
        router.push(next).catch(() => {})
    }
}

function clearDestinyPaper(): void {
    product.destinyPaperId = null
}

function handleProductTypeChange(): void {
    clearDestinyPaper()
    if (userEditedComponents.value) return
    const productTypeId = productTemplate.jsonTemplate.productTypeId
    const productType = productsList.value.find((p) => p.id === productTypeId)
    if (!productType?.productTypeComponent?.length) return
    productTemplate.jsonTemplate.orderProductComponents = productType.productTypeComponent.map((typeComponent) => {
        const newComponent = {
            component: typeComponent.component,
            componentId: typeComponent.componentId,
            orderComponentProcesses: [],
            orderComponentProperties: []
        }
        if (typeComponent.component?.code === 'skladka') newComponent.numberOfCopies = 0
        if (Array.isArray(typeComponent.productTypeComponentProcesses)) {
            typeComponent.productTypeComponentProcesses.forEach((process, index) => {
                newComponent.orderComponentProcesses.push({
                    order: process.order ?? index + 1,
                    productionProcessId: process.productionProcessId,
                    productionProcess: process.productionProcess
                })
            })
        }
        if (Array.isArray(typeComponent.productTypeComponentProperties)) {
            typeComponent.productTypeComponentProperties.forEach((typeProp) => {
                newComponent.orderComponentProperties.push({
                    productProductionPropertyId: typeProp.productProductionPropertyId,
                    productProductionProperty: typeProp.productProductionProperty,
                    value: typeProp.productProductionProperty?.defaultValue ?? ''
                })
            })
        }
        return newComponent
    })
    assignSequentialIds()
    hasProductChanges.value = true
}

function onSignalChange(signalValue: boolean | null): void {
    if (!signalValue) product.signalAmount = 0
}

function productionFileName(item: InternalOrderProductionFileAttachment): string {
    if (item?.originalFileName?.trim()) return item.originalFileName.trim()
    if (item?.urlToExternalFile) return item.urlToExternalFile
    if (!item?.file) return ''
    const parts = item.file.split('/')
    return parts[parts.length - 1] ?? ''
}

async function addProductionFileAttachment(): Promise<void> {
    const file = currentProductionFile.value
    if (!file || !canUploadProductionFile()) return
    const formData = new FormData()
    formData.append('file', file)
    productionFileUploading.value = true
    try {
        const response = await internalOrdersQueries.uploadProductionFile(formData)
        if (response?.data) product.productionFiles.push(response.data)
        currentProductionFile.value = null
        hasProductChanges.value = true
    } catch (err) {
        errorHandler(err)
    } finally {
        productionFileUploading.value = false
    }
}

async function addProductionFileAttachmentLink(): Promise<void> {
    const url = fileLink.value?.trim()
    if (!url) return
    productionFileUploading.value = true
    try {
        const response = await internalOrdersQueries.addProductionFileLink({ urlToExternalFile: url })
        if (response?.data) product.productionFiles.push(response.data)
        fileLink.value = ''
        hasProductChanges.value = true
    } catch (err) {
        errorHandler(err)
    } finally {
        productionFileUploading.value = false
    }
}

async function deleteProductionFileAttachment(id: number): Promise<void> {
    productionFileRemoving.value = true
    try {
        await internalOrdersQueries.deleteProductionFile(id)
        const fileIndex = (product.productionFiles ?? []).findIndex((item) => item.id === id)
        if (fileIndex !== -1) product.productionFiles.splice(fileIndex, 1)
        hasProductChanges.value = true
    } catch (err) {
        errorHandler(err)
    } finally {
        productionFileRemoving.value = false
    }
}

function goBack(): void {
    router.push({ name: 'ordersInternalOrdersOrderCreate' }).catch(() => {})
}

function buildProductPayload(): Record<string, unknown> {
    return {
        name: productTemplate.name,
        productTypeId: productTemplate.jsonTemplate.productTypeId,
        amount: product.amount,
        description: productTemplate.jsonTemplate.description ?? null,
        numberOfVersions: product.numberOfVersions ?? 1,
        orderProductComponents: prepareOrderProductComponentsForCreate(productTemplate.jsonTemplate.orderProductComponents ?? []),
        weight: product.weight ?? 0,
        destinyPaperId: isProofOrPackagingPrototype.value ? (product.destinyPaperId ?? null) : null,
        productionFiles: product.productionFiles ?? [],
        orderProductAttachmentId: (product.productionFiles ?? []).map((f) => f.id).filter(Boolean),
        formatA: productTemplate.jsonTemplate.formatA ?? null,
        formatB: productTemplate.jsonTemplate.formatB ?? null,
        productionFileInPathId: productTemplate.jsonTemplate.productionFileInPathId ?? null,
        signalAmount: product.signalAmount ?? 0
    }
}

function validateProductBeforeSave(): boolean {
    if (!productTemplate.name?.trim()) return false
    if (productTemplate.jsonTemplate.productTypeId == null) return false
    const amount = Number(product.amount)
    if (!Number.isFinite(amount) || amount < 1) return false
    const numVersions = Number(product.numberOfVersions)
    if (!Number.isFinite(numVersions) || numVersions < 1) return false
    if (componentsLength.value === 0) return false
    const components = productTemplate.jsonTemplate.orderProductComponents ?? []
    for (let i = 0; i < components.length; i++) {
        const component = components[i]
        if (!(component.orderComponentProcesses?.length > 0)) return false
        for (const prop of component.orderComponentProperties ?? []) {
            const propertyValue = prop.value
            if (propertyValue == null || String(propertyValue ?? '').trim() === '') return false
        }
        if (component.component?.code === 'skladka') {
            const copies = parseInt(component.numberOfCopies, 10)
            if (Number.isNaN(copies) || copies < 1) return false
        }
    }
    if (isProofOrPackagingPrototype.value && product.destinyPaperId == null) return false
    if (isSignal.value) {
        const signalAmt = Number(product.signalAmount)
        if (!Number.isFinite(signalAmt) || signalAmt < 1) return false
    }
    return true
}

async function saveProduct(): Promise<void> {
    submitted.value = true
    v$Template.value.$touch()
    v$Product.value.$touch()
    if (!isProductTypeCombinationValid.value) {
        notificationService.error(t('orders.internalOrders.product.validations.productsInSetCombination'))
        return
    }
    if (v$Template.value.$invalid || v$Product.value.$invalid || !validateProductBeforeSave()) {
        notificationService.error(t('common.validations.invalidMessage'))
        return
    }

    const productPayload = buildProductPayload()
    const templatePayload = { ...productTemplate }

    if (saveProductTemplate.value && isAddProduct.value) {
        actionLoading.value = true
        try {
            await productTemplatesService.createProductTemplate({
                name: productTemplate.name,
                jsonTemplate: JSON.stringify(productTemplate.jsonTemplate)
            })
        } catch (err) {
            errorHandler(err)
            actionLoading.value = false
            return
        }
        actionLoading.value = false
    }

    if (isAddProduct.value) {
        internalOrdersStore.addOrderCreateProduct({ product: productPayload, productTemplate: templatePayload })
    } else {
        const idx = editIndex.value
        if (idx === undefined) return
        internalOrdersStore.updateOrderCreateProduct({
            index: idx,
            product: productPayload,
            productTemplate: templatePayload
        })
    }

    hasProductChanges.value = false
    goBack()
}

// 9. Helpers / lifecycle
async function initializePage(): Promise<void> {
    await prefetchOrderProductPageData()
    initFromStore()
    hydrateTemplateComponents()
    assignSequentialIds()
    pageTitle.value = isAddProduct.value
        ? t('orders.internalOrders.addProductPageTitle')
        : t('orders.internalOrders.editProductPageTitle')
    submitted.value = false
    v$Template.value.$reset()
    v$Product.value.$reset()
}

function initFromStore(): void {
    const orderCreateData = orderCreate.value
    const productIndex = editIndex.value
    if (productIndex !== undefined && orderCreateData?.order?.orderProducts?.[productIndex] != null) {
        const orderProduct = orderCreateData.order.orderProducts[productIndex] as Record<string, unknown>
        const storedProductTemplate = orderCreateData.productTemplates?.[productIndex] as ProductTemplateForm | undefined
        Object.assign(product, {
            amount: orderProduct.amount,
            numberOfVersions: orderProduct.numberOfVersions ?? 1,
            weight: orderProduct.weight ?? 0,
            productionFiles: orderProduct.productionFiles
                ? [...(orderProduct.productionFiles as InternalOrderProductionFileAttachment[])]
                : [],
            destinyPaperId: (orderProduct.destinyPaperId as number | null | undefined) ?? null,
            signalAmount: (orderProduct.signalAmount as number | undefined) ?? 0
        })
        isSignal.value = Number(orderProduct.signalAmount ?? 0) > 0
        if (storedProductTemplate) {
            productTemplate.name = storedProductTemplate.name ?? null
            productTemplate.tag = storedProductTemplate.tag ?? null
            const nextJsonTemplate = JSON.parse(
                JSON.stringify(storedProductTemplate.jsonTemplate ?? EMPTY_PRODUCT_TEMPLATE().jsonTemplate)
            )
            // Keep `jsonTemplate` reference stable for Vuelidate bindings
            Object.assign(productTemplate.jsonTemplate, nextJsonTemplate)
            productTemplate.jsonTemplate.formatA = normalizeProductDimension(productTemplate.jsonTemplate.formatA)
            productTemplate.jsonTemplate.formatB = normalizeProductDimension(productTemplate.jsonTemplate.formatB)
        }
    } else {
        Object.assign(product, EMPTY_PRODUCT())
        const empty = EMPTY_PRODUCT_TEMPLATE()
        productTemplate.name = empty.name
        productTemplate.tag = empty.tag
        const nextEmptyJsonTemplate = JSON.parse(JSON.stringify(empty.jsonTemplate))
        // Keep `jsonTemplate` reference stable for Vuelidate bindings
        Object.assign(productTemplate.jsonTemplate, nextEmptyJsonTemplate)
    }
}

onBeforeMount(async () => {
    try {
        await initializePage()
    } catch (err) {
        errorHandler(err)
    } finally {
        isPageReady.value = true
    }
})

onBeforeRouteLeave((to, _from, next) => {
    if (hasProductChanges.value) {
        confirmLeaveNext.value = to
        confirmLeaveDialog.value = true
        next(false)
    } else {
        next()
    }
})
</script>
